import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createClient } from '@supabase/supabase-js';

import MainLayout from './pages/MainLayout';
import GalleryPage from './pages/GalleryPage';
import BlurtPage from './pages/BlurtPage';
import SettingsPage from './pages/SettingsPage';
import AppViewPage from './pages/AppViewPage';
import IntroPage from './pages/IntroPage';
import LoadingPage from './pages/LoadingPage';
import HomePage from './pages/HomePage';
import ProjectPage from './pages/ProjectPage';
import IdeasPage from './pages/IdeasPage';
import IdeaDetailPage from './pages/IdeaDetailPage';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#6366f1',
    },
    secondary: {
      main: '#f59e0b',
    },
  },
});

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase configuration. Please check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseKey);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingPublish, setPendingPublish] = useState(null);

  useEffect(() => {
    // Get initial session and setup auth listener
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event:', event, session?.user?.email);
      
      setUser(session?.user ?? null);
      setLoading(false);

      // Handle successful OAuth login
      if (event === 'SIGNED_IN' && session?.user?.app_metadata?.provider === 'google') {
        console.log('Google OAuth login successful!');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return <LoadingPage />;
  }

  const blurtPageProps = {
    user,
    pendingPublish,
    setPendingPublish
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div style={{ overflowX: 'hidden', width: '100%', maxWidth: '100vw' }}>
      <Router>
        <Routes>
          {/* Standalone app view (no layout) */}
          <Route path="/apps/:projectName" element={<AppViewPage />} />
                    
          {/* All other routes with MainLayout */}
          <Route path="/*" element={
            <MainLayout user={user}>
              <Routes>
                {/* Public routes - Ideas discovery */}
                <Route path="/" element={<IdeasPage />} />
                <Route path="/idea/:id" element={<IdeaDetailPage />} />

                {/* Auth routes */}
                <Route path="/login" element={user ? <Navigate to="/home" /> : <IntroPage />} />

                {/* Protected routes */}
                <Route path="/home" element={user ? <HomePage /> : <Navigate to="/login" />} />
                <Route path="/chat" element={user ? <ProjectPage /> : <Navigate to="/login" />} />
                <Route path="/project/:id" element={user ? <ProjectPage /> : <Navigate to="/login" />} />
              </Routes>
            </MainLayout>
          } />
        </Routes>
      </Router>
      </div>
    </ThemeProvider>
  );
}

export default App;